<template>
  <q-dialog
    v-model="visible"
    persistent
    class="dialog-xxs"
    @hide="onHide"
  >
    <q-card class="q-pa-none">
      <div class="text-h6 q-pa-sm">
        {{ $t('common.qrCodeSize') }}
      </div>
      <q-separator />
      <q-card-section>
        <div class="text-caption text-grey-7 q-mt-xs">
          {{ $t('common.selectQrCodeSize') }}
        </div>
      </q-card-section>
      <q-card-section class="q-pt-none">
        <div class="q-mt-md text-center">
          <div class="text-caption text-grey-7">
            {{ $t('common.preview') }}
          </div>
          <div
            class="qr-preview q-mt-sm q-mx-auto"
            :style="{
              width: `${previewSize}px`,
              height: `${previewSize}px`,
              border: '2px dashed #ccc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }"
          >
            <q-icon name="mdi-qrcode" :size="`${Math.max(previewSize * 0.8, 20)}px`" color="grey-5" />
          </div>
          <div class="text-caption text-grey-7 q-mt-xs">
            {{ size }}cm × {{ size }}cm
          </div>
        </div>
        <div class="row items-center q-gutter-md q-mt-lg">
          <div class="col-auto">
            <q-input
              v-model.number="size"
              type="number"
              :min="minSize"
              :max="maxSize"
              :step="0.1"
              :label="$t('common.width')"
              suffix="cm"
              dense
              style="width: 7.5rem"
              :rules="[
                val => val !== null && val !== '' || $t('validation.required'),
                val => val >= minSize || $t('validation.minValue', { min: `${minSize}cm` }),
                val => val <= maxSize || $t('validation.maxValue', { max: `${maxSize}cm` })
              ]"
              @update:model-value="validateSize"
            />
          </div>
          <div class="col">
            <q-slider
              v-model="size"
              :min="minSize"
              :max="maxSize"
              :step="0.1"
              label
              label-always
              color="primary"
              @update:model-value="validateSize"
            />
          </div>
        </div>
      </q-card-section>

      <q-card-actions align="right" class="q-pt-none">
        <q-btn
          flat
          :label="$t('common.cancel')"
          color="grey-7"
          @click="onCancel"
        />
        <q-btn
          unelevated
          :label="$t('common.confirm')"
          color="primary"
          :disable="!isValid"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  defaultSize: {
    type: Number,
    default: 3
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const visible = ref(props.modelValue)
const size = ref(props.defaultSize)
const isValid = ref(true)
const minSize = 1.5
const maxSize = 10

// Preview size in pixels (roughly 1cm = 37.8px at 96 DPI)
const previewSize = computed(() => Math.min(Math.max(size.value * 37.8, 40), 200))

const validateSize = (value) => {
  isValid.value = value >= minSize && value <= maxSize && value !== null && value !== ''
}

const onConfirm = () => {
  if (isValid.value) {
    emit('confirm', size.value)
    visible.value = false
  }
}

const onCancel = () => {
  emit('cancel')
  visible.value = false
}

const onHide = () => {
  emit('update:modelValue', false)
}

watch(() => props.modelValue, (newValue) => {
  visible.value = newValue
  if (newValue) {
    size.value = props.defaultSize
    validateSize(size.value)
  }
})

watch(visible, (newValue) => {
  emit('update:modelValue', newValue)
})

// Validate initial value
validateSize(size.value)
</script>

<style scoped>
.qr-preview {
  border-radius: 8px;
  background: #fafafa;
  transition: all 0.3s ease;
}
</style>
